import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Singleton script loader for the Rentware widget runtime.
 *
 *  - Loads https://cdn.rtr-io.com/widgets.js once (per page session).
 *  - Reads global token + locale from /api/settings/rentware (admin-managed).
 *  - Exposes a `useRentware()` hook returning { ready, configured }.
 *
 *  The widget script registers custom elements (e.g. `<rtr-order>`) that
 *  consumers can render with dangerouslySetInnerHTML, see RentwareEmbed.
 */
const SCRIPT_SRC = "https://cdn.rtr-io.com/widgets.js";
const SCRIPT_ID = "rentware-widgets-script";

let loadPromise = null;

async function ensureLoaded(token, locale) {
  if (typeof window === "undefined") return false;
  if (!token) return false;
  // expose globals BEFORE the script evaluates
  window.RTR_ACCESS_TOKEN = token;
  window.RTR_LOCALE = locale || "nb-NO";
  if (document.getElementById(SCRIPT_ID)) return true;
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.type = "module";
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return loadPromise;
}

export function useRentware() {
  const [state, setState] = useState({ ready: false, configured: false, token: "", locale: "nb-NO" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings/rentware");
        const token = r.data?.access_token || "";
        const locale = r.data?.locale || "nb-NO";
        if (cancelled) return;
        if (!token) {
          setState({ ready: false, configured: false, token: "", locale });
          return;
        }
        const ok = await ensureLoaded(token, locale);
        if (!cancelled) setState({ ready: ok, configured: true, token, locale });
      } catch {
        if (!cancelled) setState({ ready: false, configured: false, token: "", locale: "nb-NO" });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}
